import { useMemo, useState } from "react";
import { TaxLayout } from "../../components/tax/TaxLayout.js";
import {
  Button,
  Card,
  NumberField,
  SelectField,
  StatCard,
} from "../../components/design-system/index.js";
import { rothLadder } from "../../lib/util/roth-ladder.js";
import { usePreferences } from "../../lib/preferences.js";

type FilingStatus = "single" | "mfj" | "mfs" | "hoh" | "qss";

interface ConversionRow {
  year: number;
  amount_cents: number;
}

const STARTING_YEAR = 2026;

function formatCents(cents: number, hide: boolean): string {
  if (hide) return "••••";
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export function TaxRothLadderPage() {
  const { privacyMode } = usePreferences();
  const hide = privacyMode === true;

  const [filingStatus, setFilingStatus] = useState<FilingStatus>("mfj");
  const [currentAge, setCurrentAge] = useState<number>(50);
  const [baselineIncome, setBaselineIncome] = useState<number>(100_000_00);
  const [conversions, setConversions] = useState<ConversionRow[]>([
    { year: STARTING_YEAR, amount_cents: 50_000_00 },
    { year: STARTING_YEAR + 1, amount_cents: 50_000_00 },
    { year: STARTING_YEAR + 2, amount_cents: 50_000_00 },
    { year: STARTING_YEAR + 3, amount_cents: 50_000_00 },
    { year: STARTING_YEAR + 4, amount_cents: 50_000_00 },
  ]);

  const result = useMemo(() => {
    return rothLadder({
      conversions,
      current_age: currentAge,
      current_year: STARTING_YEAR,
      filing_status: filingStatus,
      baseline_taxable_income_cents: baselineIncome,
    });
  }, [conversions, currentAge, filingStatus, baselineIncome]);

  function updateConversion(index: number, amount_cents: number) {
    setConversions((prev) =>
      prev.map((c, i) => (i === index ? { ...c, amount_cents } : c)),
    );
  }
  function addYear() {
    setConversions((prev) => [
      ...prev,
      { year: prev.length === 0 ? STARTING_YEAR : prev[prev.length - 1]!.year + 1, amount_cents: 50_000_00 },
    ]);
  }

  return (
    <TaxLayout
      title="Roth conversion ladder"
      subtitle="Year-by-year conversion modeler. Each tranche becomes available 5 years after conversion (per IRC § 408A(d)(3))."
    >
      <Card className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SelectField
            label="Filing status"
            value={filingStatus}
            onChange={(e) => setFilingStatus(e.target.value as FilingStatus)}
          >
            <option value="single">Single</option>
            <option value="mfj">Married filing jointly</option>
            <option value="mfs">Married filing separately</option>
            <option value="hoh">Head of household</option>
            <option value="qss">Qualifying surviving spouse</option>
          </SelectField>
          <NumberField
            label="Current age"
            value={currentAge}
            min={30}
            max={75}
            onChange={(e) => setCurrentAge(Number(e.target.value))}
          />
          <NumberField
            label="Baseline taxable income (cents)"
            value={baselineIncome}
            min={0}
            step={1_000_00}
            onChange={(e) => setBaselineIncome(Number(e.target.value))}
            helper="Held flat across the ladder period in v1. Phase 11 polish may add per-year baseline."
          />
        </div>

        <div className="space-y-2">
          {conversions.map((c, i) => (
            <div
              key={`${c.year}-${i}`}
              className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end"
            >
              <div className="text-sm">
                <div className="text-xs text-[var(--color-muted)]">Year</div>
                <div className="font-mono">{c.year}</div>
              </div>
              <NumberField
                label={`Conversion (cents)`}
                value={c.amount_cents}
                min={0}
                step={1_000_00}
                onChange={(e) => updateConversion(i, Number(e.target.value))}
              />
              <div className="text-xs text-[var(--color-muted)]">
                <div>Available {c.year + 5}</div>
                <div>Age {currentAge + (c.year - STARTING_YEAR)} → {currentAge + (c.year - STARTING_YEAR) + 5}</div>
              </div>
              <div className="text-xs">
                {result.schedule[i] && (
                  <span className="text-[var(--color-muted)]">
                    Tax {formatCents(result.schedule[i].total_tax_cents, hide)}
                  </span>
                )}
              </div>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={addYear}>
            Add year
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard
          label="Total converted"
          value={formatCents(result.total_converted_cents, hide)}
        />
        <StatCard
          label="Lifetime ladder tax"
          value={formatCents(result.lifetime_total_tax_cents, hide)}
          sub="Sum across all conversion years"
        />
        <StatCard
          label="Baseline lifetime tax"
          value={formatCents(result.baseline_lifetime_total_tax_cents, hide)}
          sub="No conversions, same years"
        />
      </div>

      {result.schedule.length > 0 && (
        <Card className="p-5">
          <div className="text-sm font-semibold mb-3 text-[var(--color-text)]">
            Year-by-year breakdown
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-[var(--color-muted)] text-left">
              <tr>
                <th className="py-1">Year</th>
                <th className="py-1 text-right">Conversion</th>
                <th className="py-1 text-right">Federal</th>
                <th className="py-1 text-right">NY</th>
                <th className="py-1 text-right">NYC</th>
                <th className="py-1 text-right">Total tax</th>
                <th className="py-1 text-right">Marginal</th>
                <th className="py-1 text-right">Available at</th>
              </tr>
            </thead>
            <tbody>
              {result.schedule.map((row) => (
                <tr key={row.year} className="border-t border-[var(--color-border-subtle)]">
                  <td className="py-1.5 font-mono">{row.year}</td>
                  <td className="py-1.5 text-right">{formatCents(row.conversion_cents, hide)}</td>
                  <td className="py-1.5 text-right">{formatCents(row.federal_tax_cents, hide)}</td>
                  <td className="py-1.5 text-right">{formatCents(row.ny_tax_cents, hide)}</td>
                  <td className="py-1.5 text-right">{formatCents(row.nyc_tax_cents, hide)}</td>
                  <td className="py-1.5 text-right font-medium">
                    {formatCents(row.total_tax_cents, hide)}
                  </td>
                  <td className="py-1.5 text-right">{row.composite_marginal_rate_pct.toFixed(1)}%</td>
                  <td className="py-1.5 text-right">{row.available_at_year} (age {row.available_at_age})</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </TaxLayout>
  );
}
